import { useStore } from '@/store';
import { storeToRefs } from 'pinia';
import { getMedia } from "@/util";

// BabylonJS Imports
import { Vector3, Color3 } from "@babylonjs/core/Maths/math";
import { Animation } from "@babylonjs/core/Animations/animation";
import { Texture } from "@babylonjs/core/Materials/Textures/texture"
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial'
import { ActionManager } from "@babylonjs/core/Actions/actionManager";
import { ExecuteCodeAction } from '@babylonjs/core/Actions/directActions';
import { CubicEase, EasingFunction } from "@babylonjs/core/Animations/easing";
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { ArcRotateCameraKeyboardMoveInput } from '@babylonjs/core/Cameras/Inputs/arcRotateCameraKeyboardMoveInput';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import "@babylonjs/core/Culling/ray";

// Interface replacing Vue Props
export interface PinProps {
    id: string;
    position: { x: number, y: number, z: number };
    alpha: number;
    beta: number;
    target: { x: number, y: number, z: number } | null;
    area: VersionDataArea | null; // Ensure VersionDataArea is defined in your types
}

// Module state
let pinPrototype: Mesh | null = null;
const ease = new CubicEase();
ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

/**
 * Initializes the prototype (Master Mesh & Material).
 * Call this ONCE when the scene loads.
 */
export const initPinSystem = () => {
    if (pinPrototype) return; // Prevent double init

    console.log("Initializing Pin System Prototypes...");
    
    // Create the master geometry
    // We use size 1 as base, scaling will be applied in createPin
    const plane = MeshBuilder.CreatePlane("pin_prototype", { size: 1 }, window.scene);
    
    // Create Material & Texture ONCE
    const material = new StandardMaterial("pinMat_Prototype", window.scene);
    const texture = new Texture(getMedia("textures/btn_pin.png"), window.scene);
    
    material.opacityTexture = texture;
    material.diffuseTexture = texture;
    material.emissiveColor = new Color3(1, 1, 1); // equivalent to 255,255,255
    material.disableLighting = true;
    material.backFaceCulling = false; // Usually good for planes/sprites
    material.freeze(); // Optimize static material

    plane.material = material;
    plane.billboardMode = 7; // Mesh.BILLBOARDMODE_ALL
    plane.setEnabled(false); // Hide master
    
    // Prevent matrix calc for master
    plane.freezeWorldMatrix(); 
    
    pinPrototype = plane;
};

/**
 * Creates a specific Pin instance based on the prototype.
 */
export const createPin = (props: PinProps) => {
    // Ensure System is initialized
    if (!pinPrototype) initPinSystem();

    const store = useStore();
    const { isMobile } = storeToRefs(store);

    console.groupCollapsed("Creating Pin_" + props.id + " for area '" + (props.area ? props.area.nodeName : "Orbit") + "'");

    // Clone the prototype (Clones share geometry + material = Performance)
    const pin = pinPrototype!.clone("pin_plane_" + props.id);
    pin.setEnabled(true); // Make clone visible
    pin.checkCollisions = false; // Optimize
    pin.doNotSyncBoundingInfo = true; // Optimize if static

    // Logic from original script
    let size = isMobile.value ? 5 : 1.8;
    let sizeSmall = isMobile.value ? 1 : 0.5;
    const finalSize = props.area != null ? sizeSmall : size;

    // Apply Scaling (since base size is 1)
    pin.scaling = new Vector3(finalSize, finalSize, finalSize);

    // Apply Position
    pin.position = new Vector3(props.position.x, props.position.y, props.position.z);
    
    if (isMobile.value && props.area == null)
        pin.position.y += 1;

    // Parenting
    if (props.area != null) {
        pin.parent = window.scene.getNodeByName("Pins_" + props.area.nodeName);
    } else {
        pin.parent = window.scene.getNodeByName("Pins_Orbit");
    }

    // Visibility Logic
    if (props.area != null) {
        setPinVisiblity(pin.name, 0, 0);
    } else {
        setPinVisiblity(pin.name, 1, 0);
    }

    // Interaction / ActionManager
    // We create a new ActionManager per pin because the actions contain specific logic (closures)
    pin.actionManager = new ActionManager(window.scene);
    
    // Hover In
    pin.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOverTrigger, () => {
        Animation.CreateAndStartAnimation("hover", pin, "scaling", 60, 20, pin.scaling, new Vector3(finalSize * 1.2, finalSize * 1.2, finalSize * 1.2), Animation.ANIMATIONLOOPMODE_CONSTANT, ease);
    }));

    // Hover Out
    pin.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOutTrigger, () => {
        Animation.CreateAndStartAnimation("hoverOut", pin, "scaling", 60, 20, pin.scaling, new Vector3(finalSize, finalSize, finalSize), Animation.ANIMATIONLOOPMODE_CONSTANT, ease);
    }));

    // Click
    pin.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPickUpTrigger, () => {
        moveCameraToPosition(pin, props);
    }));

    console.groupEnd();

    return pin;
};

// ==========================================
// Helper / Logic Functions
// ==========================================

const moveCameraToPosition = (pin: AbstractMesh, props: PinProps) => {
    console.log("Start Camera Movement to " + pin.name);

    // Set keyboard controls
    const camera = window.scene.activeCamera as ArcRotateCamera;
    if (camera.inputs && camera.inputs.attached.keyboard) {
        let keyboardInputs = camera.inputs.attached.keyboard as ArcRotateCameraKeyboardMoveInput;
        keyboardInputs.keysDown = [38];
        keyboardInputs.keysUp = [40];
        keyboardInputs.keysLeft = [39];
        keyboardInputs.keysRight = [37];
    }

    const store = useStore();
    const { versionData, isFirstTimeTutorial } = storeToRefs(store);

    if (props.area) { // MOVE TO PIN
        animateCamerarideToPin(pin, props, 2000);

        // Hide the pin that was clicked
        setPinGroupVisible("Pins_" + props.area.nodeName, 0, [], 20);
        setPinGroupVisible("Pins_" + props.area.nodeName, 1, [pin.name], 20);
    } else { // MOVE TO AREA
        // Set Active AreaID
        store.setActiveAreaID(parseInt(props.id.split("-")[1]));

        // Disable all orbital pins
        setPinGroupVisible("Pins_Orbit", 0, [], 20);

        // Disable Environment (Clouds/Birds)
        setEnvironmentVisible(0, 20);

        // Animate Camera
        let time = 2000; // in ms
        animateCamerarideToArea(props, time);

        // At Animationend (ca. 1.5 Seconds)
        setTimeout(() => {
            if (!versionData.value) return;

            // Enable all Pins in PinGroup for this area
            let targetArea = versionData.value.areas[Number(props.id.split("-")[1])];
            setPinGroupVisible("Pins_" + targetArea.nodeName, 1, ["pin_plane_1-0", "pin_plane_2-0"], 20);

            // Enable tooltips
            store.setToolTipsDisabled(false);

            // Enable Navigation UI
            let navigationUI = document.getElementById("navigation");
            if (navigationUI) {
                navigationUI.style.opacity = "1";
                navigationUI.style.pointerEvents = "all";
            }

            if (isFirstTimeTutorial.value)
                store.setTutorialVisible(true);
        }, time);
    }
};

const animateCamerarideToArea = (props: PinProps, time: number) => {
    const store = useStore();
    const { versionData } = storeToRefs(store);
    
    if(!versionData.value) return;
    
    console.log("Animate Camera to " + versionData.value.areas[Number(props.id.split("-")[1])].nodeName);

    const camera = window.scene.activeCamera as ArcRotateCamera;

    // Open Limit ranges
    camera.lowerRadiusLimit = camera.upperRadiusLimit = null;
    camera.lowerAlphaLimit = camera.upperAlphaLimit = null;
    camera.lowerBetaLimit = camera.upperBetaLimit = null;

    // Animate to Center
    const framePerSecond = 60;
    var totalFrames = time * framePerSecond / 1000;
    let totalFramesSlow = totalFrames * 1.5;

    var alphaAnimation = new Animation("camAlpha", "alpha", framePerSecond, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);
    alphaAnimation.setEasingFunction(ease)
    alphaAnimation.setKeys([
        { frame: 0, value: camera.alpha % (Math.PI * 2) },
        { frame: camera.beta > 0.9 ? totalFramesSlow : totalFrames, value: props.alpha }
    ]);

    var betaAnimation = new Animation("camBeta", "beta", framePerSecond, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);
    betaAnimation.setEasingFunction(ease)
    betaAnimation.setKeys([
        { frame: 0, value: camera.beta % (Math.PI * 2) },
        { frame: camera.beta > 0.9 ? totalFramesSlow / 2 : totalFrames / 3, value: camera.beta > 0.9 ? 0.7 : camera.beta % (Math.PI * 2) },
        { frame: camera.beta > 0.9 ? totalFramesSlow : totalFrames, value: props.beta }
    ]);

    var radiusAnimation = new Animation("camRadius", "radius", framePerSecond, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);
    radiusAnimation.setEasingFunction(ease)
    radiusAnimation.setKeys([
        { frame: 2, value: camera.radius },
        { frame: camera.beta > 0.9 ? totalFramesSlow : totalFrames, value: 0 }
    ]);

    camera.animations = [
        alphaAnimation,
        betaAnimation,
        radiusAnimation,
    ];

    if (props.target) {
        var targetAnimation = new Animation("camTarget", "target", framePerSecond, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CYCLE);
        targetAnimation.setEasingFunction(ease)
        targetAnimation.setKeys([
            { frame: 0, value: camera.target },
            { frame: camera.beta > 0.9 ? totalFramesSlow : totalFrames, value: new Vector3(props.target.x, props.target.y, props.target.z) }
        ]);
        camera.animations.push(targetAnimation);
    }

    window.scene.beginAnimation(camera, 0, camera.beta > 0.9 ? totalFramesSlow : totalFrames);
};

const animateCamerarideToPin = (pin: AbstractMesh, props: PinProps, time: number) => {
    // Calculate total frames
    const framePerSecond = 60;
    var totalFrames = time * framePerSecond / 1000;

    // Animate Camera
    const camera = window.scene.activeCamera as ArcRotateCamera;
    Animation.CreateAndStartAnimation("camToPinTarget", camera, "target", 60, totalFrames, camera.target, pin.position.add(new Vector3(0, 1, 0)), Animation.ANIMATIONLOOPMODE_CONSTANT, ease);
    Animation.CreateAndStartAnimation("camToPinRadius", camera, "radius", 60, totalFrames, camera.radius, 0, Animation.ANIMATIONLOOPMODE_CONSTANT, ease);
    Animation.CreateAndStartAnimation("camToPinAlpha", camera, "alpha", 60, totalFrames, camera.alpha % (Math.PI * 2), props.alpha, Animation.ANIMATIONLOOPMODE_CONSTANT, ease);
    Animation.CreateAndStartAnimation("camToPinBeta", camera, "beta", 60, totalFrames, camera.beta % (Math.PI * 2), props.beta, Animation.ANIMATIONLOOPMODE_CONSTANT, ease);
};

const setPinVisiblity = (name: string, visibility: number, time = 40) => {
    // console.log("Set Pin Visible: " + name + " to " + (visibility != 0));
    let pin = window.scene.getMeshByName(name);
    if (pin) {
        if (visibility != 0) pin.setEnabled(visibility != 0);
        Animation.CreateAndStartAnimation("pinVis", pin, "visibility", 60, time, pin.visibility, visibility, Animation.ANIMATIONLOOPMODE_CONSTANT, ease, () => {
            pin!.setEnabled(visibility != 0);
        });
    }
};

const setPinGroupVisible = (name: string, visibility: number, exceptions: string[], time = 40) => {
    console.groupCollapsed("Set Pin Group Visible: " + name + " to " + visibility + " with exceptions: [" + exceptions + "]");
    let group = window.scene.getNodeByName(name);
    if (group) {
        group.getChildren().forEach((child) => {
            if (!exceptions.find(exception => exception === child.name)) setPinVisiblity(child.name, visibility, time);
        });
    }
    console.groupEnd();
};

const setEnvironmentVisible = (visibility: number, time = 40) => {
    console.log("Set Environment Visible: " + visibility);
    let environmentNode = window.scene.getNodeByName("Environment");
    if (environmentNode) {
        environmentNode.getChildren().forEach(mesh => {
            Animation.CreateAndStartAnimation("envVis", mesh, "visibility", 60, time, (mesh as AbstractMesh).visibility, visibility, Animation.ANIMATIONLOOPMODE_CONSTANT, ease);
        });
    }
};